import React, { useEffect, useMemo, useState } from "react";

import {
	Box,
	Button,
	CircularProgress,
	Divider,
	IconButton,
	Switch,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { SettingsContainer, StyleCircle } from "./styles";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	useDeleteWeddingInvitationMutation,
	useLazyGetManageWeddingInvitationQuery,
	useRequestPublishInvitationMutation,
	useUpdateInvitationMutation
} from "../../../store/services/invitations/invitations.service";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
	getFullInvitationsState,
	setOpenPricingModal,
	setSelectedField
} from "../../../store/slices/invitations/invitations.slice";
import PassCodeModal from "../../Modals/PassCodeModal";
import {
	INVITATION_ENTRANCE,
	INVITATION_OCCASION,
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_STATUS,
	INVITATION_STATUSES,
	Invitations
} from "../../../store/services/invitations/dto/invitations.dto";
import ConfirmModal from "../../Modals/ConfirmModal";
// import { formatCurrency } from "../../../utils/formatCurrency";
// import { getInvitationPrice } from "../../../utils/getInvitationPrice";
import { toast } from "react-toastify";
import { trackEvent } from "../../../utils/analytics";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../utils/tiktokPixel";
import { InvitationStatusColor } from "../../../constants";
import SectionsConfig from "./SectionsConfig";
import EntranceConfig from "./EntranceConfig";
import NotificationsConfig from "../../Modals/NotificationsConfig";
import InvitationColorPicker from "../../InvitationColorPicker";
import { TemplateColorObject } from "../../../InvitationColorMatrix";
import { GoldCrown } from "../../../Pages/Invitations/styles";
import { getFieldMap } from "../../../utils/inputs";

const SettingItem = ({
	label,
	value,
	action,
	style,
	actionStyle
}: {
	label: string | React.ReactNode;
	value: React.ReactNode;
	action: React.ReactNode;
	style?: React.CSSProperties;
	actionStyle?: React.CSSProperties;
}) => {
	return (
		<Box className={"setting_item"} style={style}>
			<Box className={"setting_content"}>
				<Box className={"setting_label"}>{label}</Box>
				{value}
			</Box>
			<Box className={"action"} style={actionStyle}>
				{action}
			</Box>
		</Box>
	);
};

const Settings = ({ handleLoadingChange }: { handleLoadingChange: (status: boolean) => void }) => {
	const [openPassCodeModal, setOpenPassCodeModal] = useState(false);
	const [openSectionsVisibility, setOpenSectionsVisibility] = useState(false);
	const [openEntranceConfig, setOpenEntranceConfig] = useState(false);
	const [openEnableNotifications, setOpenEnableNotifications] = useState(false);
	const [makePublic, setMakePublic] = useState(false);
	const [requestPublish, setRequestPublish] = useState(false);
	const [deleteInvitation, setDeleteInvitation] = useState(false);
	const { managedInvitation, invitationPayedData } = useAppSelector(getFullInvitationsState);
	const [templateName, setTemplateName] = useState("");
	const [callUpdateInvitation, { isLoading: isUpdating }] = useUpdateInvitationMutation();
	const [callRequestPublishInvitation, { isLoading: isRequestPublishInvitation }] =
		useRequestPublishInvitationMutation();
	const [callDeleteWeddingInvitation, { isLoading: isDeletingWeddingInvitation }] =
		useDeleteWeddingInvitationMutation();
	const [fetchInvitation, { isLoading: isFetchingInvitation }] = useLazyGetManageWeddingInvitationQuery();
	const params = useParams();
	const navigate = useNavigate();
	const invitationId = params.id || "";
	const location = useLocation();
	const isDashboardRute = location.pathname.includes("dashboard/settings");
	const dispatch = useAppDispatch();
	const fieldMap = getFieldMap(invitationId);
	useEffect(() => {
		if (!managedInvitation) {
			fetchInvitation({ id: invitationId || "" });
		} else {
			setTemplateName(managedInvitation?.name);
		}
	}, [invitationId, managedInvitation]);

	useEffect(() => {
		if (templateName === "" || templateName === managedInvitation?.name) return;

		const timeout = setTimeout(() => {
			handleUpdateSetting({ name: templateName });
		}, 2000); // 2 seconds

		return () => {
			clearTimeout(timeout);
		};
	}, [templateName]);

	useEffect(() => {
		handleLoadingChange(isUpdating);
	}, [isUpdating]);

	const disablePublishBtn = useMemo(() => {
		if (!managedInvitation) return true;
		return (
			managedInvitation.status !== INVITATION_STATUS.PUBLISHED &&
			(!managedInvitation.fieldUpdate?.groom_name ||
				!managedInvitation.fieldUpdate?.bride_name ||
				!managedInvitation.fieldUpdate?.wedding_date ||
				(!managedInvitation.fieldUpdate?.wedding_coordinates && !managedInvitation.fieldUpdate?.wedding_location_text))
		);
	}, [managedInvitation]);

	const AlbumTitle = useMemo(() => {
		if (!managedInvitation) return "";
		switch (managedInvitation?.occasion) {
			case INVITATION_OCCASION.KATB_KTAB: {
				return "Akd Karaan Abum";
			}
			case INVITATION_OCCASION.ENGAGEMENT: {
				return "Engagement Album";
			}
			default: {
				return "Wedding Album";
			}
		}
	}, [managedInvitation]);
	const handleUpdateSetting = (options: Partial<Invitations>, cb?: () => void) => {
		callUpdateInvitation({ _id: invitationId, ...options }).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setOpenPassCodeModal(false);
				setMakePublic(false);
				if (cb) cb();
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleChangeLang = (event: React.MouseEvent<HTMLElement>, newLang: string) => {
		handleUpdateSetting({ language: newLang });
	};

	const handleInvitationOccasionChange = (event: React.MouseEvent<HTMLElement>, invOccasion: string) => {
		handleUpdateSetting({ occasion: Number(invOccasion) });
	};

	const showPassCodeModal = () => {
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE) {
			return dispatch(setOpenPricingModal(true));
		}
		if (managedInvitation?.is_private) {
			setMakePublic(true);
		} else {
			setOpenPassCodeModal(true);
		}
	};
	const handlePrivacyChange = (password: string[] | null) => {
		handleUpdateSetting({
			is_private: !!password,
			...(!!password && { password: password.join("") })
		});
	};

	const handleRequestPublish = (cancel?: boolean) => {
		if (cancel) {
			// unpublish
			trackEvent("un_publish_invitation", {
				scope: "Invitations",
				description: "Un Publish Invitation"
			});
			trackMetaEvent(
				"un_publish_invitation",
				{ invitation_id: invitationId, currency: managedInvitation?.currency, value: 0 },
				true
			);
			trackTikTokEvent("un_publish_invitation", {
				invitation_id: invitationId
			});
		} else {
			trackEvent("publish_invitation", {
				scope: "Invitations",
				description: "Publish Invitation"
			});
			trackMetaEvent(
				"publish_invitation",
				{ invitation_id: invitationId, currency: managedInvitation?.currency, value: 0 },
				true
			);
			trackTikTokEvent("publish_invitation", {
				invitation_id: invitationId
			});
		}
		callRequestPublishInvitation({ _id: invitationId!, cancel }).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setRequestPublish(false);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleRequestPublishInvitation = () => {
		setRequestPublish(true);
	};

	const showIslamicVersesToggle = () => {
		handleUpdateSetting({ is_islamic: !managedInvitation?.is_islamic });
	};

	const enableWeddingAlbumToggle = () => {
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE) {
			return dispatch(setOpenPricingModal(true));
		}
		handleUpdateSetting({ wedding_album: !managedInvitation?.wedding_album });
	};

	const handleUpdateInvitationEntrance = (entrance: INVITATION_ENTRANCE) => {
		console.log("Selected entrance:", INVITATION_ENTRANCE[entrance], entrance);
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.ESSENTIAL) {
			return dispatch(setOpenPricingModal(true));
		}
		handleUpdateSetting({ entrance: entrance }, () => {
			toast.success("Invitation entrance updated successfully", { autoClose: 3000 });
		});
	};

	const enableHasImagesToggle = () => {
		handleUpdateSetting({ has_images: !managedInvitation?.has_images });
	};

	const handleEditSocialShareImage = () => {
		dispatch(setSelectedField(fieldMap.THUMBNAIL_02));
	};

	const handleDeleteWeddingInvitation = () => {
		callDeleteWeddingInvitation({ _id: invitationId! }).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setDeleteInvitation(false);
				toast.success("Invitation has been permanently deleted.", { autoClose: 3000 });
				setTimeout(() => {
					navigate("/myAccount/invitations", { replace: true });
				}, 500);
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};
	const handleUpdateColor = (color: TemplateColorObject) => {
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.ESSENTIAL) {
			return dispatch(setOpenPricingModal(true));
		}
		handleUpdateSetting({ template_color: color.id });
	};

	const handleOpenNotifications = () => {
		if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE) {
			return dispatch(setOpenPricingModal(true));
		}
		setOpenEnableNotifications(true);
	};

	if (isFetchingInvitation) {
		return (
			<Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"30vh"}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<SettingsContainer width={isDashboardRute ? "100%" : "90%"}>
			<Box my={1} mt={3} width={"100%"}>
				<TextField
					label={"Template Id"}
					disabled={true}
					fullWidth={true}
					value={invitationId}
					sx={{
						"& .MuiOutlinedInput-root": {
							background: "#fff"
						}
					}}
				/>
			</Box>
			<Box my={1} mt={3} width={"100%"}>
				<TextField
					label={"Template name"}
					fullWidth={true}
					value={templateName}
					onChange={e => setTemplateName(e.target.value)}
					sx={{
						"& .MuiOutlinedInput-root": {
							background: "#fff"
						}
					}}
				/>
			</Box>
			<Box my={1}>
				<Divider />
			</Box>
			<SettingItem
				label={"Status"}
				value={
					<Box className={"setting_value"}>
						<Box display={"flex"} alignItems={"center"} gap={1}>
							<StyleCircle $color={InvitationStatusColor[managedInvitation?.status || 1]} />
							<Typography>{INVITATION_STATUSES[managedInvitation?.status || 1]}</Typography>
						</Box>
					</Box>
				}
				action={
					(managedInvitation?.status === INVITATION_STATUS.DRAFT ||
						managedInvitation?.status === INVITATION_STATUS.PENDING ||
						managedInvitation?.status === INVITATION_STATUS.PUBLISHED ||
						managedInvitation?.status === INVITATION_STATUS.ACTION_REQUIRED) && (
						<Button
							variant={"contained"}
							color={"primary"}
							size={"small"}
							disabled={isRequestPublishInvitation || disablePublishBtn}
							onClick={handleRequestPublishInvitation}
						>
							{managedInvitation?.status === INVITATION_STATUS.DRAFT ? "Publish" : "Un Publish"}
							{/*{managedInvitation?.status === INVITATION_STATUS.DRAFT &&*/}
							{/*	`(${formatCurrency(getInvitationPrice(managedInvitation.price, managedInvitation.is_discount_percentage, managedInvitation.discount), managedInvitation.currency)})`}*/}
						</Button>
					)
				}
			/>
			{managedInvitation?.status === INVITATION_STATUS.PENDING && (
				<Box className={"info"}>
					<Typography variant={"h6"}>Invitation Under Review </Typography>
					<Typography>You’ll be notified by email once it’s approved. This process may take up to 24 hours.</Typography>
				</Box>
			)}
			{managedInvitation?.status === INVITATION_STATUS.REJECTED && (
				<Box className={"info"}>
					<Typography variant={"h6"}>Invitation Rejected </Typography>
					<Typography>
						After reviewing the content, we’re unable to approve it for publication at this time. <br /> please feel
						free to contact our support team
					</Typography>
				</Box>
			)}
			{managedInvitation?.status === INVITATION_STATUS.SUSPENDED && (
				<Box className={"info"}>
					<Typography variant={"h6"}>Invitation Suspended </Typography>
					<Typography>
						This invitation has been suspended by our team due to policy or content concerns. Please contact our support
						team if you believe this was a mistake or need further assistance.
					</Typography>
				</Box>
			)}
			{/*<Box my={1} display={"flex"} justifyContent={"center"}>*/}
			<SettingItem
				label={"Occasion"}
				value={<></>}
				style={{ flexDirection: "column", alignItems: "flex-start", gap: 5 }}
				actionStyle={{ margin: "auto" }}
				action={
					<ToggleButtonGroup
						fullWidth
						color="primary"
						value={(managedInvitation?.occasion || "").toString() || "1"}
						exclusive
						onChange={handleInvitationOccasionChange}
						aria-label="Invitation Occasion"
						disabled={isUpdating}
					>
						<ToggleButton value="1">Wedding</ToggleButton>
						<ToggleButton value="3">Akd Karaan</ToggleButton>
						<ToggleButton value="2">Engagement</ToggleButton>
					</ToggleButtonGroup>
				}
			/>
			{/*</Box>*/}
			<Box my={1}>
				<Divider />
			</Box>
			<SettingItem
				label={
					<Box display={"flex"} alignItems={"center"} gap={1}>
						Theme{" "}
						{invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.ESSENTIAL && (
							<GoldCrown width={"20px"} height={"20px"} />
						)}
					</Box>
				}
				value={<></>}
				style={{ flexDirection: "column", alignItems: "flex-start", gap: 5 }}
				action={
					<InvitationColorPicker
						templateId={managedInvitation?.template_id || 1}
						template_color={managedInvitation?.template_color || 1}
						onColorPick={handleUpdateColor}
					/>
				}
				actionStyle={{ margin: "auto" }}
			/>
			<Box my={1}>
				<Divider />
			</Box>
			<Box onClick={() => setOpenEntranceConfig(true)}>
				<SettingItem
					label={
						<Box display={"flex"} alignItems={"center"} gap={1}>
							Entrance
							{invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.ESSENTIAL && (
								<GoldCrown width={"20px"} height={"20px"} />
							)}
						</Box>
					}
					value={
						<Box className={"setting_value"}>
							<Typography>Choose how guests unlock your invitation</Typography>
						</Box>
					}
					action={
						<IconButton>
							<SettingsIcon />
						</IconButton>
					}
				/>
			</Box>
			<Box my={1}>
				<Divider />
			</Box>
			<Box onClick={handleOpenNotifications}>
				<SettingItem
					label={
						<Box display={"flex"} alignItems={"center"} gap={1}>
							Notifications{" "}
							{invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE && (
								<GoldCrown width={"20px"} height={"20px"} />
							)}
						</Box>
					}
					value={
						<Box className={"setting_value"}>
							<Typography>Enable notifications to receive alerts for new RSVP</Typography>
						</Box>
					}
					action={
						<IconButton>
							<SettingsIcon />
						</IconButton>
					}
				/>
			</Box>
			<Box my={1}>
				<Divider />
			</Box>
			<SettingItem
				label={"Language"}
				value={<></>}
				action={
					<ToggleButtonGroup
						color="primary"
						value={managedInvitation?.language || "EN"}
						exclusive
						onChange={handleChangeLang}
						aria-label="Platform"
						disabled={isUpdating}
					>
						<ToggleButton value="EN">EN</ToggleButton>
						<ToggleButton value="AR">AR</ToggleButton>
					</ToggleButtonGroup>
				}
			/>
			{!isDashboardRute && (
				<Box onClick={handleEditSocialShareImage}>
					<SettingItem
						label={"Social Share Image"}
						value={
							<Box className={"setting_value"}>
								<Typography>upload an image to use when sharing your invitation on social media</Typography>
							</Box>
						}
						action={
							<IconButton>
								<SettingsIcon />
							</IconButton>
						}
					/>
				</Box>
			)}
			<SettingItem
				label={"Images"}
				value={
					<Box className={"setting_value"}>
						<Typography>Show/Hide invitation images</Typography>
					</Box>
				}
				action={
					<Switch checked={managedInvitation?.has_images} disabled={isUpdating} onChange={enableHasImagesToggle} />
				}
			/>
			<SettingItem
				label={
					<Box display={"flex"} alignItems={"center"} gap={1}>
						<Typography>{AlbumTitle}</Typography>
						{invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE && (
							<GoldCrown width={"20px"} height={"20px"} />
						)}
					</Box>
				}
				value={
					<Box className={"setting_value"}>
						<Typography>Guest can upload photos or videos to the album</Typography>
					</Box>
				}
				action={
					<Switch
						checked={managedInvitation?.wedding_album}
						disabled={isUpdating}
						onChange={enableWeddingAlbumToggle}
					/>
				}
			/>
			<SettingItem
				label={"Islamic oriented"}
				value={
					<Box className={"setting_value"}>
						<Typography>Show Islamic verses.</Typography>
					</Box>
				}
				action={
					<Switch checked={managedInvitation?.is_islamic} disabled={isUpdating} onChange={showIslamicVersesToggle} />
				}
			/>
			<SettingItem
				label={
					<Box display={"flex"} alignItems={"center"} gap={1}>
						Private invitation{" "}
						{invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.SIGNATURE && (
							<GoldCrown width={"20px"} height={"20px"} />
						)}
					</Box>
				}
				value={
					<Box className={"setting_value"}>
						<Typography>Secure your invitation with a password.</Typography>
					</Box>
				}
				action={<Switch checked={managedInvitation?.is_private} disabled={isUpdating} onChange={showPassCodeModal} />}
			/>
			<Box my={1}>
				<Divider />
			</Box>
			<Box onClick={() => setOpenSectionsVisibility(true)}>
				<SettingItem
					label={"Sections visibility"}
					value={
						<Box className={"setting_value"}>
							<Typography>Show or Hide sections</Typography>
						</Box>
					}
					action={
						<IconButton>
							<SettingsIcon />
						</IconButton>
					}
				/>
			</Box>
			<Box mt={"auto"} mb={2}>
				<Divider />
			</Box>
			<Button
				variant={"contained"}
				color={"error"}
				size={"small"}
				disabled={isDeletingWeddingInvitation}
				onClick={() => setDeleteInvitation(true)}
			>
				Delete Invitation
			</Button>
			{openPassCodeModal && (
				<PassCodeModal
					title={"Create Password"}
					open={true}
					onClose={() => setOpenPassCodeModal(false)}
					onSave={handlePrivacyChange}
					loading={isUpdating}
				/>
			)}
			{makePublic && (
				<ConfirmModal
					open={true}
					title={"Invitation Privacy"}
					message={"Are you sure you want to make this invitation public?"}
					onConfirm={() => handlePrivacyChange(null)}
					onClose={() => setMakePublic(false)}
					confirmText={"Yes, Change Privacy"}
					cancelText={"No, Cancel"}
					loading={isUpdating}
				/>
			)}
			{requestPublish && (
				<ConfirmModal
					open={true}
					title={`${managedInvitation?.status === INVITATION_STATUS.DRAFT ? "Publish" : "Un Publish"} Invitation`}
					message={`Are you sure you want to ${managedInvitation?.status === INVITATION_STATUS.DRAFT ? "Publish" : "Un Publish"} this invitation?`}
					onConfirm={() => {
						const shouldCancel = managedInvitation?.status !== INVITATION_STATUS.DRAFT;
						handleRequestPublish(shouldCancel);
					}}
					onClose={() => setRequestPublish(false)}
					confirmText={`Yes, ${managedInvitation?.status === INVITATION_STATUS.DRAFT ? "Publish" : "Un Publish"}`}
					cancelText={"No, Cancel"}
					loading={isRequestPublishInvitation}
				/>
			)}
			{deleteInvitation && (
				<ConfirmModal
					open={true}
					title={"Delete Invitation"}
					message={
						"Are you sure you want to delete this invitation?  Once you delete this invitation, all its data will be permanently removed."
					}
					onConfirm={handleDeleteWeddingInvitation}
					onClose={() => setDeleteInvitation(false)}
					confirmText={"Yes, Delete"}
					cancelText={"No, Cancel"}
					loading={isDeletingWeddingInvitation}
				/>
			)}
			{openSectionsVisibility && (
				<SectionsConfig
					onClose={() => setOpenSectionsVisibility(false)}
					managedInvitation={managedInvitation || ({} as Invitations)}
					handleUpdate={handleUpdateSetting}
				/>
			)}
			{openEnableNotifications && <NotificationsConfig onClose={() => setOpenEnableNotifications(false)} />}
			{openEntranceConfig && (
				<EntranceConfig
					onClose={() => setOpenEntranceConfig(false)}
					managedInvitation={managedInvitation || ({} as Invitations)}
					handleSelectEntrance={handleUpdateInvitationEntrance}
				/>
			)}
		</SettingsContainer>
	);
};

export default Settings;
